import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI, usersAPI, dronesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { format, startOfMonth, endOfMonth, startOfDay, endOfDay, subDays, eachDayOfInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import { Clock, Plus, Trash2, Calendar as CalendarIcon, FileText, Download, Plane, User, Play, Square, BarChart3, TrendingUp, MapPin, FileSpreadsheet, Bell, CheckSquare, XSquare, FileDown, Car, StickyNote, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import WeatherBriefingWidget from '../components/WeatherBriefingWidget';

const API_URL = '/api';

const CHART_COLORS = ['#C8A400', '#4A90D9', '#16A34A', '#F59E0B', '#EF4444', '#8B5CF6'];

const SITUACOES = [
  'Casa com piscina',
  'Desvio de agua',
  'Ponto verde',
  'Vazamento na rede',
  'Vazamento na caixa de ventosa',
  'Vazamento na caixa de descarga',
  'Vazamento na caixa de hidrometro',
  'Vazamento na caixa de equilibrio',
  'Vazamento na caixa de registro',
  'Vazamento na caixa de derivacao',
  'Torre da YGUA',
  'Caixa elevada YGUA',
  'Bomba booster YGUA',
  'Elevatoria IGUA',
  'TAU YGUA'
];

const TimePage = () => {
  const { isSuperAdmin, isAdmin, isSupervisor, isPilot, user, token } = useAuth();
  const canManageReports = isSuperAdmin || isAdmin;
  const [flightLogs, setFlightLogs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('daily');
  const [showAddModal, setShowAddModal] = useState(false);
  const [onlinePilots, setOnlinePilots] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [newLog, setNewLog] = useState({
    date: new Date(), shift: 'morning', pilot_id: '', drone_id: '',
    start_time: '', end_time: '', km: '', occurrence: '', city: '', community: '',
    not_performed: false, anotacoes: '', km_vtr_start: '', km_vtr_end: '',
    registros: []
  });
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [userProject, setUserProject] = useState(null);
  const wsRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [logsRes, projectsRes, usersRes, dronesRes] = await Promise.all([
        axios.get(`${API_URL}/flight-logs`, { headers }),
        canManageReports ? projectsAPI.list() : Promise.resolve({ data: [] }),
        canManageReports ? usersAPI.list() : Promise.resolve({ data: [] }),
        dronesAPI.list()
      ]);
      setFlightLogs(logsRes.data);
      setProjects(projectsRes.data);
      setUsers(usersRes.data.filter(u => u.role === 'pilot'));
      setDrones(dronesRes.data);
      // Find current user's project for logo display
      if (projectsRes.data.length > 0) {
        const pid = user?.project_id;
        const proj = pid ? projectsRes.data.find(p => p.id === pid) : projectsRes.data[0];
        if (proj) setUserProject(proj);
      }
      if (isPilot && user?.id) setSelectedUser(user.id);
      if (canManageReports) {
        try {
          const [onlineRes, notifRes] = await Promise.all([
            axios.get(`${API_URL}/pilots/online`, { headers }),
            axios.get(`${API_URL}/notifications`, { headers })
          ]);
          setOnlinePilots(onlineRes.data);
          setNotifications(notifRes.data.filter(n => !n.read));
        } catch (e) { console.log('Online pilots/notifications fetch failed'); }
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [token, canManageReports, isPilot, user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!canManageReports || !token) return;
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//${window.location.host}`;
    const ws = new WebSocket(`${wsUrl}/ws/notifications`);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'flight_alert' || data.type === 'flight_registered') {
          const notif = data.notification;
          setNotifications(prev => {
            if (prev.some(n => n.id === notif.id)) return prev;
            return [notif, ...prev];
          });
          toast.info(notif.message, { duration: 6000 });
          fetchData();
        }
      } catch (e) { console.log('WS parse error'); }
    };
    ws.onerror = () => {};
    wsRef.current = ws;
    return () => { ws.close(); };
  }, [canManageReports, token, fetchData]);

  useEffect(() => {
    if (!token) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [token, fetchData]);

  const handleCreateFlightLog = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...newLog,
        date: newLog.date.toISOString(),
        km: parseFloat(newLog.km) || 0,
        km_vtr_start: newLog.km_vtr_start !== '' ? parseFloat(newLog.km_vtr_start) : null,
        km_vtr_end: newLog.km_vtr_end !== '' ? parseFloat(newLog.km_vtr_end) : null,
        project_id: user?.project_id || selectedProject || projects[0]?.id || '',
        pilot_id: isPilot ? user.id : newLog.pilot_id,
        registros: newLog.registros || []
      };
      await axios.post(`${API_URL}/flight-logs`, payload, { headers });
      toast.success('Registro de voo adicionado!');
      setShowAddModal(false);
      setNewLog({
        date: new Date(), shift: 'morning', pilot_id: '', drone_id: '',
        start_time: '', end_time: '', km: '', occurrence: '', city: '', community: '',
        not_performed: false, anotacoes: '', km_vtr_start: '', km_vtr_end: '',
        registros: []
      });
      fetchData();
    } catch (error) { toast.error('Erro ao adicionar registro'); }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await axios.delete(`${API_URL}/flight-logs/${logId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Registro excluido!');
      setSelectedLogs(prev => { const next = new Set(prev); next.delete(logId); return next; });
      fetchData();
    } catch (error) { toast.error('Erro ao excluir'); }
  };

  const handleBulkDelete = async (logIds) => {
    const count = logIds.length;
    if (!window.confirm(`Excluir ${count} registro(s) selecionado(s)?`)) return;
    const headers = { Authorization: `Bearer ${token}` };
    let deleted = 0;
    for (const id of logIds) {
      try { await axios.delete(`${API_URL}/flight-logs/${id}`, { headers }); deleted++; } catch (e) {}
    }
    toast.success(`${deleted} registro(s) excluido(s)!`);
    setSelectedLogs(new Set());
    fetchData();
  };

  const toggleLogSelection = (logId) => {
    setSelectedLogs(prev => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId); else next.add(logId);
      return next;
    });
  };

  const toggleAllLogs = () => {
    if (selectedLogs.size === filteredLogs.length) setSelectedLogs(new Set());
    else setSelectedLogs(new Set(filteredLogs.map(l => l.id)));
  };

  const calculateFlightDuration = (start, end) => {
    if (!start || !end) return { text: '-', minutes: 0 };
    const [sH, sM] = start.split(':').map(Number);
    const [eH, eM] = end.split(':').map(Number);
    let total = (eH * 60 + eM) - (sH * 60 + sM);
    if (total < 0) total += 24 * 60;
    return { text: `${Math.floor(total / 60)}h ${total % 60}m`, minutes: total };
  };

  const getFilteredLogs = () => {
    let filtered = [...flightLogs];
    if (selectedProject && selectedProject !== 'all') filtered = filtered.filter(l => l.project_id === selectedProject);
    if (selectedUser && selectedUser !== 'all') filtered = filtered.filter(l => l.pilot_id === selectedUser);
    if (filterType === 'daily') {
      const dS = startOfDay(selectedDate), dE = endOfDay(selectedDate);
      filtered = filtered.filter(l => { const d = new Date(l.date); return d >= dS && d <= dE; });
    } else {
      const mS = startOfMonth(selectedDate), mE = endOfMonth(selectedDate);
      filtered = filtered.filter(l => { const d = new Date(l.date); return d >= mS && d <= mE; });
    }
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const filteredLogs = getFilteredLogs();

  const stats = (() => {
    let totalMin = 0, totalKm = 0, totalVtrKm = 0, notPerformed = 0, withNotes = 0, withOccurrences = 0, totalRegistros = 0;
    const situacaoCounts = {};
    filteredLogs.forEach(l => {
      totalMin += calculateFlightDuration(l.start_time, l.end_time).minutes;
      totalKm += l.km || 0;
      if (l.not_performed) notPerformed++;
      if (l.anotacoes) withNotes++;
      if (l.occurrence) withOccurrences++;
      if (l.km_vtr_start != null && l.km_vtr_end != null) {
        totalVtrKm += (l.km_vtr_end - l.km_vtr_start);
      }
      const regs = l.registros || [];
      totalRegistros += regs.length;
      regs.forEach(r => {
        const sit = r.situacao || 'Sem situacao';
        situacaoCounts[sit] = (situacaoCounts[sit] || 0) + 1;
      });
    });
    const realized = filteredLogs.length - notPerformed;
    return {
      totalFlights: filteredLogs.length, realized, notPerformed,
      totalTime: `${Math.floor(totalMin / 60)}h ${totalMin % 60}m`,
      totalKm: totalKm.toFixed(1), totalVtrKm: totalVtrKm.toFixed(1),
      activePilots: new Set(filteredLogs.map(l => l.pilot_id)).size,
      activeDrones: new Set(filteredLogs.map(l => l.drone_id)).size,
      withNotes, withOccurrences, totalRegistros, situacaoCounts
    };
  })();

  const chartData = (() => {
    // Shift distribution
    const shiftData = [
      { name: 'Manha', value: filteredLogs.filter(l => l.shift === 'morning').length },
      { name: 'Tarde', value: filteredLogs.filter(l => l.shift === 'afternoon').length },
      { name: 'Noite', value: filteredLogs.filter(l => l.shift === 'night').length }
    ];

    // Last 7 days
    const last7 = eachDayOfInterval({ start: subDays(new Date(), 6), end: new Date() });
    const dailyData = last7.map(day => ({
      name: format(day, 'dd/MM'),
      voos: flightLogs.filter(l => { const d = new Date(l.date); return d >= startOfDay(day) && d <= endOfDay(day); }).length
    }));

    // KM por piloto (Top 5)
    const kmByPilot = {};
    filteredLogs.forEach(l => { kmByPilot[l.pilot_id] = (kmByPilot[l.pilot_id] || 0) + (l.km || 0); });
    const pilotKmData = Object.entries(kmByPilot).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([pid, km]) => {
      const p = users.find(u => u.id === pid);
      return { name: p?.name?.split(' ')[0] || 'Piloto', km: Math.round(km) };
    });

    // Duracao por voo (Top 15)
    const durationData = filteredLogs.filter(l => !l.not_performed && l.start_time && l.end_time).slice(0, 15).map((l, i) => {
      const dur = calculateFlightDuration(l.start_time, l.end_time);
      return { name: l.date ? format(new Date(l.date), 'dd/MM') : `#${i + 1}`, minutos: dur.minutes };
    });

    // Status (realized vs unrealized)
    const statusData = [
      { name: 'Realizados', value: stats.realized },
      { name: 'Nao Realizados', value: stats.notPerformed }
    ];

    // Notas e Ocorrencias
    const notesOccData = [
      { name: 'Anotacoes', value: stats.withNotes },
      { name: 'Ocorrencias', value: stats.withOccurrences }
    ];

    // VTR KM por dia
    const vtrByDay = {};
    filteredLogs.forEach(l => {
      if (l.km_vtr_start != null && l.km_vtr_end != null) {
        const day = l.date ? l.date.substring(0, 10) : 'unknown';
        vtrByDay[day] = (vtrByDay[day] || 0) + (l.km_vtr_end - l.km_vtr_start);
      }
    });
    const vtrData = Object.entries(vtrByDay).sort((a, b) => a[0].localeCompare(b[0])).slice(-10).map(([day, km]) => ({
      name: day.length >= 10 ? `${day.substring(8, 10)}/${day.substring(5, 7)}` : day,
      km: Math.round(km)
    }));

    // KM de voo por voo
    const flightKmData = filteredLogs.filter(l => !l.not_performed && l.km > 0).slice(0, 15).map((l, i) => ({
      name: l.date ? format(new Date(l.date), 'dd/MM') : `#${i + 1}`,
      km: l.km
    }));

    // Situacao distribution
    const situacaoData = Object.entries(stats.situacaoCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({ name: name.length > 20 ? name.substring(0, 20) + '...' : name, value, fullName: name }));

    return { shiftData, dailyData, pilotKmData, durationData, statusData, notesOccData, vtrData, flightKmData, situacaoData };
  })();

  const getPilotName = (pid) => { if (pid === user?.id) return user.name || 'Voce'; const p = users.find(u => u.id === pid); return p?.name || p?.username || '-'; };
  const getDroneName = (did) => { const d = drones.find(dr => dr.id === did); return d?.serial_number || '-'; };
  const getLogoUrl = (logoPath) => !logoPath || !token ? null : `${API_URL}/files/${logoPath}?auth=${token}`;

  const exportPDF = () => {
    const params = new URLSearchParams({ period: filterType });
    if (selectedProject && selectedProject !== 'all') params.append('project_id', selectedProject);
    if (selectedUser && selectedUser !== 'all') params.append('pilot_id', selectedUser);
    fetch(`${API_URL}/reports/flight-logs/pdf?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `jornada_rpas_${format(new Date(), 'yyyyMMdd')}.pdf`; a.click(); toast.success('PDF exportado!'); })
      .catch(() => toast.error('Erro ao exportar PDF'));
  };

  const exportExcel = () => {
    const params = new URLSearchParams({ period: filterType });
    if (selectedProject && selectedProject !== 'all') params.append('project_id', selectedProject);
    if (selectedUser && selectedUser !== 'all') params.append('pilot_id', selectedUser);
    fetch(`${API_URL}/reports/flight-logs/excel?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `jornada_rpas_${format(new Date(), 'yyyyMMdd')}.xlsx`; a.click(); toast.success('Excel exportado!'); })
      .catch(() => toast.error('Erro ao exportar Excel'));
  };

  const exportWord = () => {
    const params = new URLSearchParams({ period: filterType });
    if (selectedProject && selectedProject !== 'all') params.append('project_id', selectedProject);
    if (selectedUser && selectedUser !== 'all') params.append('pilot_id', selectedUser);
    fetch(`${API_URL}/reports/flight-logs/word?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob()).then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `jornada_rpas_${format(new Date(), 'yyyyMMdd')}.docx`; a.click(); toast.success('Word exportado!'); })
      .catch(() => toast.error('Erro ao exportar Word'));
  };

  const exportCSV = () => {
    const logs = getFilteredLogs();
    let csv = '\uFEFF';
    csv += `RELATORIO DE VOOS - ${filterType === 'daily' ? 'DIARIO' : 'MENSAL'}\nData: ${format(new Date(), 'dd/MM/yyyy HH:mm')}\n\n`;
    csv += `Data,Turno,Piloto,RPA,Inicio,Termino,Duracao,KM Voo,KM VTR Ini,KM VTR Fim,Status,Ocorrencia,Anotacoes,Registros\n`;
    logs.forEach(l => {
      const d = calculateFlightDuration(l.start_time, l.end_time);
      const regs = (l.registros || []).map(r => `${r.p_numero}: ${r.situacao}`).join('; ');
      csv += `${format(new Date(l.date), 'dd/MM/yyyy')},${l.shift === 'morning' ? 'Manha' : l.shift === 'afternoon' ? 'Tarde' : 'Noite'},${getPilotName(l.pilot_id)},${getDroneName(l.drone_id)},${l.start_time || '-'},${l.end_time || '-'},${d.text},${l.km || 0},${l.km_vtr_start ?? '-'},${l.km_vtr_end ?? '-'},${l.not_performed ? 'N/R' : 'OK'},"${l.occurrence || ''}","${l.anotacoes || ''}","${regs}"\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `relatorio_voos_${filterType}_${format(selectedDate, filterType === 'daily' ? 'dd-MM-yyyy' : 'MM-yyyy')}.csv`; a.click();
    toast.success('CSV exportado!');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-gold/30 border-t-gold rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="time-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          {userProject?.logo_url && (
            <img src={getLogoUrl(userProject.logo_url)} alt="" className="w-12 h-12 object-contain rounded-lg border border-gray-200 bg-white p-1 shadow-sm" data-testid="jornada-header-logo" />
          )}
          <div>
            <h2 className="text-xl font-rajdhani font-semibold text-navy uppercase tracking-wider flex items-center gap-2">
              <Clock size={20} className="text-gold" /> REGISTRO DE JORNADA
            </h2>
            {userProject && <p className="text-gray-400 text-xs font-mono mt-0.5">{userProject.name}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {isPilot && (
            <Button className="bg-red-600 hover:bg-red-500 text-white rounded-lg" onClick={async () => {
              try { await axios.post(`${API_URL}/pilot/flight-alert`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success('Alerta INICIANDO VOO enviado!'); } catch (e) { toast.error('Erro ao enviar alerta'); }
            }} data-testid="flight-alert-btn"><Bell size={16} className="mr-2" /> INICIANDO VOO</Button>
          )}
          {isPilot && (
            <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
              <DialogTrigger asChild><Button className="btn-primary" data-testid="add-flight-btn"><Plus size={18} className="mr-2" />REGISTRAR VOO</Button></DialogTrigger>
              <DialogContent className="bg-white border border-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="text-navy font-rajdhani uppercase tracking-wider">REGISTRAR VOO</DialogTitle></DialogHeader>
                <form onSubmit={handleCreateFlightLog} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-600 font-mono text-xs uppercase">Data</Label>
                      <Popover><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start text-left input-hud"><CalendarIcon className="mr-2 h-4 w-4 text-gold" />{format(newLog.date, "dd/MM/yyyy", { locale: ptBR })}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={newLog.date} onSelect={(d) => setNewLog({ ...newLog, date: d || new Date() })} locale={ptBR} /></PopoverContent></Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-600 font-mono text-xs uppercase">Turno</Label>
                      <Select value={newLog.shift} onValueChange={(v) => setNewLog({ ...newLog, shift: v })}><SelectTrigger className="input-hud"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="morning">Manha</SelectItem><SelectItem value="afternoon">Tarde</SelectItem><SelectItem value="night">Noite</SelectItem></SelectContent></Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600 font-mono text-xs uppercase">RPA / Drone</Label>
                    <Select value={newLog.drone_id} onValueChange={(v) => setNewLog({ ...newLog, drone_id: v })}><SelectTrigger className="input-hud"><SelectValue placeholder="Selecione o drone" /></SelectTrigger><SelectContent>{drones.map(d => <SelectItem key={d.id} value={d.id}>{d.serial_number} - {d.model}</SelectItem>)}</SelectContent></Select>
                  </div>

                  {/* Voo nao realizado toggle */}
                  <div className="flex items-center gap-3 p-3 bg-red-50/50 rounded-lg border border-red-100" data-testid="not-performed-toggle">
                    <input type="checkbox" id="not-performed" checked={newLog.not_performed}
                      onChange={(e) => setNewLog({ ...newLog, not_performed: e.target.checked, start_time: e.target.checked ? '' : newLog.start_time, end_time: e.target.checked ? '' : newLog.end_time, km: e.target.checked ? '' : newLog.km })}
                      className="w-4 h-4 rounded border-gray-300 accent-red-500" />
                    <label htmlFor="not-performed" className="cursor-pointer text-sm font-medium text-red-600">Voo nao realizado</label>
                  </div>

                  {!newLog.not_performed && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2"><Label className="text-gray-600 font-mono text-xs uppercase">Inicio</Label><Input type="time" value={newLog.start_time} onChange={(e) => setNewLog({ ...newLog, start_time: e.target.value })} className="input-hud" data-testid="flight-start-time" /></div>
                      <div className="space-y-2"><Label className="text-gray-600 font-mono text-xs uppercase">Termino</Label><Input type="time" value={newLog.end_time} onChange={(e) => setNewLog({ ...newLog, end_time: e.target.value })} className="input-hud" data-testid="flight-end-time" /></div>
                      <div className="space-y-2"><Label className="text-gray-600 font-mono text-xs uppercase flex items-center gap-1"><MapPin size={12} /> KM Voo</Label><Input type="number" step="0.1" min="0" value={newLog.km} onChange={(e) => setNewLog({ ...newLog, km: e.target.value })} className="input-hud" placeholder="0.0" data-testid="flight-km-input" /></div>
                    </div>
                  )}

                  {/* KM da VTR */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-600 font-mono text-xs uppercase flex items-center gap-1"><Car size={12} /> KM VTR Inicio</Label>
                      <Input type="number" step="0.1" min="0" value={newLog.km_vtr_start} onChange={(e) => setNewLog({ ...newLog, km_vtr_start: e.target.value })} className="input-hud" placeholder="KM odometro inicio" data-testid="vtr-km-start" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-600 font-mono text-xs uppercase flex items-center gap-1"><Car size={12} /> KM VTR Fim</Label>
                      <Input type="number" step="0.1" min="0" value={newLog.km_vtr_end} onChange={(e) => setNewLog({ ...newLog, km_vtr_end: e.target.value })} className="input-hud" placeholder="KM odometro fim" data-testid="vtr-km-end" />
                    </div>
                  </div>

                  <div className="space-y-2"><Label className="text-gray-600 font-mono text-xs uppercase">Ocorrencia</Label><Input value={newLog.occurrence} onChange={(e) => setNewLog({ ...newLog, occurrence: e.target.value })} className="input-hud" placeholder="Ocorrencia do voo..." data-testid="flight-occurrence" /></div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-gray-600 font-mono text-xs uppercase">Cidade</Label><Input value={newLog.city} onChange={(e) => setNewLog({ ...newLog, city: e.target.value })} className="input-hud" placeholder="Ex: Manaus" data-testid="flight-city-input" /></div>
                    <div className="space-y-2"><Label className="text-gray-600 font-mono text-xs uppercase">Comunidade</Label><Input value={newLog.community} onChange={(e) => setNewLog({ ...newLog, community: e.target.value })} className="input-hud" placeholder="Ex: Comunidade Norte" data-testid="flight-community-input" /></div>
                  </div>

                  {/* Anotacoes */}
                  <div className="space-y-2">
                    <Label className="text-gray-600 font-mono text-xs uppercase flex items-center gap-1"><StickyNote size={12} /> Anotacoes</Label>
                    <Textarea value={newLog.anotacoes} onChange={(e) => setNewLog({ ...newLog, anotacoes: e.target.value })} className="input-hud min-h-[60px]" placeholder="Observacoes, notas adicionais..." data-testid="flight-anotacoes" />
                  </div>

                  {/* Registros (P-Nº / Situação) */}
                  <div className="space-y-3 p-4 bg-blue-50/50 rounded-lg border border-blue-100" data-testid="registros-section">
                    <div className="flex items-center justify-between">
                      <Label className="text-gray-600 font-mono text-xs uppercase flex items-center gap-1"><ClipboardList size={14} /> REGISTROS ({newLog.registros.length})</Label>
                      <Button type="button" size="sm" className="bg-blue-500 hover:bg-blue-400 text-white text-xs h-7 px-3 rounded-lg"
                        onClick={() => {
                          setNewLog({ ...newLog, registros: [...newLog.registros, { p_numero: '', situacao: '' }] });
                        }} data-testid="add-registro-btn">
                        <Plus size={14} className="mr-1" /> ADICIONAR
                      </Button>
                    </div>
                    {newLog.registros.length > 0 && (
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {newLog.registros.map((reg, idx) => (
                          <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded-lg border border-blue-100 animate-fade-in" data-testid={`registro-row-${idx}`}>
                            <Input
                              value={reg.p_numero}
                              onChange={(e) => {
                                const updated = [...newLog.registros];
                                updated[idx] = { ...updated[idx], p_numero: e.target.value };
                                setNewLog({ ...newLog, registros: updated });
                              }}
                              className="w-[80px] h-8 text-xs font-mono text-blue-600 font-bold border-blue-200 text-center"
                              placeholder="P-01"
                              data-testid={`p-numero-input-${idx}`}
                            />
                            <Select value={reg.situacao} onValueChange={(v) => {
                              const updated = [...newLog.registros];
                              updated[idx] = { ...updated[idx], situacao: v };
                              setNewLog({ ...newLog, registros: updated });
                            }}>
                              <SelectTrigger className="flex-1 h-8 text-xs border-blue-200" data-testid={`situacao-select-${idx}`}>
                                <SelectValue placeholder="Selecione a situacao..." />
                              </SelectTrigger>
                              <SelectContent>
                                {SITUACOES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                              onClick={() => {
                                const updated = newLog.registros.filter((_, i) => i !== idx);
                                setNewLog({ ...newLog, registros: updated });
                              }} data-testid={`remove-registro-${idx}`}>
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {newLog.registros.length === 0 && (
                      <p className="text-gray-400 text-xs text-center py-2 font-mono">Nenhum registro adicionado</p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="btn-secondary flex-1">CANCELAR</Button>
                    <Button type="submit" className="btn-primary flex-1" data-testid="submit-flight-btn">REGISTRAR</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Online Pilots */}
      {canManageReports && onlinePilots.length > 0 && (
        <div className="card-hud p-4 border-green-200 bg-green-50/50 animate-slide-up" data-testid="online-pilots">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-green-700 font-rajdhani font-semibold text-sm uppercase tracking-wider">PILOTOS ONLINE ({onlinePilots.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {onlinePilots.map(p => (
              <div key={p.id} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-green-200 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-navy font-medium">{p.name || p.username}</span>
                <span className="text-gray-400 text-xs font-mono">{p.clock_in_time ? new Date(p.clock_in_time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flight Alerts */}
      {canManageReports && notifications.length > 0 && (
        <div className="card-hud p-4 border-amber-200 bg-amber-50/50 animate-slide-up" data-testid="flight-alerts">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Bell size={16} className="text-amber-600" /><span className="text-amber-700 font-rajdhani font-semibold text-sm uppercase tracking-wider">ALERTAS DE VOO ({notifications.length})</span></div>
            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-800 text-xs" onClick={async () => {
              const headers = { Authorization: `Bearer ${token}` };
              await Promise.all(notifications.map(n => axios.put(`${API_URL}/notifications/${n.id}/read`, {}, { headers })));
              setNotifications([]);
            }} data-testid="clear-alerts-btn">LIMPAR</Button>
          </div>
          <div className="space-y-2">
            {notifications.slice(0, 5).map(n => (
              <div key={n.id} className="flex items-center gap-3 bg-white px-3 py-2 rounded-lg border border-amber-200 text-sm animate-fade-in">
                <Plane size={14} className="text-amber-600" /><span className="text-navy font-medium">{n.message}</span>
                <span className="text-gray-400 text-xs font-mono ml-auto">{new Date(n.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-hud p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-2">
            <Label className="text-gray-500 font-mono text-xs uppercase">Periodo</Label>
            <Select value={filterType} onValueChange={setFilterType}><SelectTrigger className="w-[140px] input-hud"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Diario</SelectItem><SelectItem value="monthly">Mensal</SelectItem></SelectContent></Select>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-500 font-mono text-xs uppercase">Data</Label>
            <Popover><PopoverTrigger asChild><Button variant="outline" className="w-[160px] justify-start text-left input-hud"><CalendarIcon className="mr-2 h-4 w-4 text-gold" />{filterType === 'daily' ? format(selectedDate, "dd/MM/yyyy", { locale: ptBR }) : format(selectedDate, "MMM yyyy", { locale: ptBR })}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={selectedDate} onSelect={(d) => setSelectedDate(d || new Date())} locale={ptBR} /></PopoverContent></Popover>
          </div>
          {canManageReports && projects.length > 0 && (
            <div className="space-y-2">
              <Label className="text-gray-500 font-mono text-xs uppercase">Projeto</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-[180px] input-hud" data-testid="filter-project"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todos os projetos</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {canManageReports && (
            <div className="space-y-2">
              <Label className="text-gray-500 font-mono text-xs uppercase">Piloto</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-[180px] input-hud" data-testid="filter-pilot"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todos os pilotos</SelectItem>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.username}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1" />
          {canManageReports && (
            <div className="flex gap-2 flex-wrap" data-testid="export-buttons">
              <Button onClick={exportPDF} className="bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 text-xs rounded-lg" disabled={filteredLogs.length === 0} data-testid="export-pdf-btn"><FileText size={14} className="mr-1" /> PDF</Button>
              <Button onClick={exportExcel} className="bg-green-50 border border-green-200 text-green-700 hover:bg-green-100 text-xs rounded-lg" disabled={filteredLogs.length === 0} data-testid="export-excel-btn"><FileSpreadsheet size={14} className="mr-1" /> EXCEL</Button>
              <Button onClick={exportWord} className="bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 text-xs rounded-lg" disabled={filteredLogs.length === 0} data-testid="export-word-btn"><FileDown size={14} className="mr-1" /> WORD</Button>
              <Button onClick={exportCSV} className="btn-secondary text-xs" disabled={filteredLogs.length === 0} data-testid="export-csv-btn"><Download size={14} className="mr-1" /> CSV</Button>
            </div>
          )}
        </div>
      </div>

      {/* Weather Briefing Widget - Jorginho Meteorologia */}
      <WeatherBriefingWidget
        token={token}
        defaultLocation={
          (selectedProject !== 'all' && projects.find(p => p.id === selectedProject)?.city) ||
          (projects[0]?.city) ||
          ''
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-gray-100 border border-gray-200 p-1 grid grid-cols-2 w-fit">
          <TabsTrigger value="logs" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm"><FileText size={14} className="mr-2" />PLANILHA</TabsTrigger>
          <TabsTrigger value="charts" className="data-[state=active]:bg-white data-[state=active]:text-gold data-[state=active]:shadow-sm"><BarChart3 size={14} className="mr-2" />GRAFICOS</TabsTrigger>
        </TabsList>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mt-6">
          <div className="card-hud p-4 animate-slide-up"><p className="text-gray-500 text-xs font-mono uppercase">Total Voos</p><p className="text-2xl font-rajdhani font-bold text-navy" data-testid="stat-total-flights">{stats.totalFlights}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.05s'}}><p className="text-gray-500 text-xs font-mono uppercase">Realizados</p><p className="text-2xl font-rajdhani font-bold text-green-600" data-testid="stat-realized">{stats.realized}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.1s'}}><p className="text-gray-500 text-xs font-mono uppercase">N/Realizados</p><p className="text-2xl font-rajdhani font-bold text-red-500" data-testid="stat-not-performed">{stats.notPerformed}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.15s'}}><p className="text-gray-500 text-xs font-mono uppercase">Tempo Total</p><p className="text-2xl font-rajdhani font-bold text-gold">{stats.totalTime}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.2s'}}><p className="text-gray-500 text-xs font-mono uppercase flex items-center gap-1"><MapPin size={12} /> KM Voo</p><p className="text-2xl font-rajdhani font-bold text-blue-600">{stats.totalKm}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.25s'}}><p className="text-gray-500 text-xs font-mono uppercase flex items-center gap-1"><Car size={12} /> KM VTR</p><p className="text-2xl font-rajdhani font-bold text-cyan-600" data-testid="stat-vtr-km">{stats.totalVtrKm}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.3s'}}><p className="text-gray-500 text-xs font-mono uppercase">Pilotos</p><p className="text-2xl font-rajdhani font-bold text-purple-600">{stats.activePilots}</p></div>
          <div className="card-hud p-4 animate-slide-up" style={{animationDelay:'0.35s'}}><p className="text-gray-500 text-xs font-mono uppercase flex items-center gap-1"><ClipboardList size={12} /> Pontos</p><p className="text-2xl font-rajdhani font-bold text-blue-500" data-testid="stat-total-registros">{stats.totalRegistros}</p></div>
        </div>

        <TabsContent value="logs" className="mt-6">
          <div className="card-hud overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 flex-wrap gap-3">
              <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider flex items-center gap-2"><FileText size={18} className="text-gold" />PLANILHA DE VOOS</h3>
              <div className="flex items-center gap-3">
                {(isSuperAdmin || isAdmin) && selectedLogs.size > 0 && (
                  <div className="flex items-center gap-2 animate-fade-in">
                    <span className="text-gray-500 text-xs font-mono">{selectedLogs.size} selecionado(s)</span>
                    {isSuperAdmin && <Button onClick={() => handleBulkDelete([...selectedLogs])} className="bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 text-xs rounded-lg h-8 px-3" data-testid="delete-selected-btn"><Trash2 size={12} className="mr-1" /> EXCLUIR SELECIONADOS</Button>}
                    <Button onClick={() => setSelectedLogs(new Set())} variant="ghost" className="text-gray-400 hover:text-gray-600 text-xs h-8 px-2" data-testid="clear-selection-btn"><XSquare size={12} className="mr-1" /> LIMPAR</Button>
                  </div>
                )}
                {isSuperAdmin && filteredLogs.length > 0 && selectedLogs.size === 0 && (
                  <Button onClick={() => handleBulkDelete(filteredLogs.map(l => l.id))} className="bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 text-xs rounded-lg h-8 px-3" data-testid="delete-all-btn"><Trash2 size={12} className="mr-1" /> DELETAR TODOS</Button>
                )}
                <span className="text-gray-500 text-xs font-mono">{filteredLogs.length} registro(s)</span>
              </div>
            </div>
            {filteredLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="flight-logs-table">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {isSuperAdmin && <th className="p-3 w-10">
                        <button onClick={toggleAllLogs} className="text-gray-400 hover:text-gold transition-colors" data-testid="select-all-checkbox">
                          {selectedLogs.size === filteredLogs.length && filteredLogs.length > 0 ? <CheckSquare size={16} className="text-gold" /> : <Square size={16} />}
                        </button>
                      </th>}
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Data</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Turno</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Piloto</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">RPA</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Inicio</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Termino</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Duracao</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">KM Voo</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">KM VTR</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Status</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Ocorrencia</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Anotacoes</th>
                      <th className="text-left p-3 text-gray-500 font-mono text-xs uppercase">Registros</th>
                      <th className="text-right p-3 text-gray-500 font-mono text-xs uppercase">Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log) => {
                      const dur = calculateFlightDuration(log.start_time, log.end_time);
                      const isSelected = selectedLogs.has(log.id);
                      const vtrKm = (log.km_vtr_start != null && log.km_vtr_end != null) ? `${log.km_vtr_start} - ${log.km_vtr_end}` : '-';
                      return (
                        <tr key={log.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-gold/5' : ''}`} data-testid={`flight-log-row-${log.id}`}>
                          {isSuperAdmin && <td className="p-3">
                            <button onClick={() => toggleLogSelection(log.id)} className="text-gray-400 hover:text-gold transition-colors" data-testid={`select-log-${log.id}`}>
                              {isSelected ? <CheckSquare size={16} className="text-gold" /> : <Square size={16} />}
                            </button>
                          </td>}
                          <td className="p-3 text-navy font-mono text-sm">{format(new Date(log.date), 'dd/MM/yyyy')}</td>
                          <td className="p-3"><span className={`badge-status ${log.shift === 'morning' ? 'bg-amber-100 text-amber-700 border-amber-200' : log.shift === 'afternoon' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-purple-100 text-purple-700 border-purple-200'}`}>{log.shift === 'morning' ? 'Manha' : log.shift === 'afternoon' ? 'Tarde' : 'Noite'}</span></td>
                          <td className="p-3 text-gray-600 text-sm"><div className="flex items-center gap-2"><User size={14} className="text-blue-500" />{getPilotName(log.pilot_id)}</div></td>
                          <td className="p-3 text-gray-600 text-sm"><div className="flex items-center gap-2"><Plane size={14} className="text-green-500" />{getDroneName(log.drone_id)}</div></td>
                          <td className="p-3 text-green-600 font-mono text-sm"><div className="flex items-center gap-1"><Play size={12} />{log.start_time || '-'}</div></td>
                          <td className="p-3 text-red-500 font-mono text-sm"><div className="flex items-center gap-1"><Square size={12} />{log.end_time || '-'}</div></td>
                          <td className="p-3 text-gold font-mono text-sm font-bold">{dur.text}</td>
                          <td className="p-3 text-blue-600 font-mono text-sm"><div className="flex items-center gap-1"><MapPin size={12} />{log.km || 0}</div></td>
                          <td className="p-3 text-cyan-600 font-mono text-sm"><div className="flex items-center gap-1"><Car size={12} />{vtrKm}</div></td>
                          <td className="p-3">{log.not_performed ? <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] font-mono font-bold rounded">N/R</span> : <span className="px-2 py-0.5 bg-green-100 text-green-600 text-[10px] font-mono font-bold rounded">OK</span>}</td>
                          <td className="p-3 text-gray-400 text-xs max-w-[150px] truncate" title={log.occurrence || ''}>{log.occurrence || '-'}</td>
                          <td className="p-3 text-gray-400 text-xs max-w-[150px] truncate" title={log.anotacoes || ''}>{log.anotacoes || '-'}</td>
                          <td className="p-3 text-xs max-w-[180px]">
                            {(log.registros?.length > 0) ? (
                              <div className="space-y-0.5">
                                {log.registros.slice(0, 3).map((r, ri) => (
                                  <div key={ri} className="flex items-center gap-1">
                                    <span className="text-blue-600 font-mono font-bold text-[10px]">{r.p_numero}</span>
                                    <span className="text-gray-500 text-[10px] truncate">{r.situacao}</span>
                                  </div>
                                ))}
                                {log.registros.length > 3 && <span className="text-gray-400 text-[10px] font-mono">+{log.registros.length - 3} mais</span>}
                              </div>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="p-3 text-right">{isSuperAdmin && <Button variant="ghost" size="icon" onClick={() => handleDeleteLog(log.id)} className="text-gray-400 hover:text-red-500 hover:bg-red-50 h-8 w-8" data-testid={`delete-log-${log.id}`}><Trash2 size={14} /></Button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center"><Clock size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-500 font-mono">Nenhum registro encontrado</p></div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="charts" className="mt-6" data-testid="charts-tab">
          {/* Row 1: Duration, Flight KM, VTR KM */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="card-hud p-4 animate-scale-in" data-testid="chart-duration">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">Duracao dos Voos (min)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.durationData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v} min`, 'Duracao']} />
                  <Bar dataKey="minutos" fill="#C8A400" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.1s'}} data-testid="chart-flight-km">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">KM por Voo</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.flightKmData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v} km`, 'KM']} />
                  <Bar dataKey="km" fill="#4A90D9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.2s'}} data-testid="chart-vtr-km">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">KM VTR por Dia</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.vtrData.length > 0 ? chartData.vtrData : [{ name: '-', km: 0 }]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip formatter={(v) => [`${v} km`, 'VTR KM']} />
                  <Bar dataKey="km" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 2: Status, Shifts, Notes/Occurrences */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.15s'}} data-testid="chart-status">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">Status (Realizado / N. Realizado)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={chartData.statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    <Cell fill="#16A34A" />
                    <Cell fill="#EF4444" />
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.25s'}} data-testid="chart-shifts">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">Voos por Turno</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.shiftData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]}>
                    {chartData.shiftData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={['#F59E0B', '#F97316', '#8B5CF6'][index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.3s'}} data-testid="chart-notes-occurrences">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">Anotacoes e Ocorrencias</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.notesOccData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    <Cell fill="#C8A400" />
                    <Cell fill="#EF4444" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 3: Daily flights, KM per pilot, Situacoes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.35s'}} data-testid="chart-daily">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">Ultimos 7 Dias</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.dailyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip formatter={(v) => [`${v}`, 'Voos']} />
                  <Bar dataKey="voos" fill="#4A90D9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.4s'}} data-testid="chart-pilot-km">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">KM por Piloto (Top 5)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.pilotKmData.length > 0 ? chartData.pilotKmData : [{ name: '-', km: 0 }]} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip formatter={(v) => [`${v} km`, 'KM']} />
                  <Bar dataKey="km" fill="#16A34A" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-hud p-4 animate-scale-in" style={{animationDelay:'0.45s'}} data-testid="chart-situacoes">
              <h4 className="text-gray-500 text-xs font-mono uppercase mb-3">Situacoes Registradas (Top 10)</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.situacaoData.length > 0 ? chartData.situacaoData : [{ name: '-', value: 0 }]} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 8 }} width={120} />
                  <Tooltip formatter={(v) => [`${v}`, 'Qtd']} />
                  <Bar dataKey="value" fill="#8B5CF6" radius={[0, 4, 4, 0]}>
                    {chartData.situacaoData.map((_, index) => (
                      <Cell key={`cell-sit-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Summary */}
          <div className="card-hud p-6 animate-slide-up" data-testid="period-summary">
            <h3 className="text-navy font-rajdhani font-semibold uppercase tracking-wider mb-4 flex items-center gap-2"><TrendingUp size={18} className="text-gold" />RESUMO DO PERIODO</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Media Voos/Dia</p><p className="text-2xl font-rajdhani font-bold text-navy">{filterType === 'daily' ? stats.totalFlights : (stats.totalFlights / 30).toFixed(1)}</p></div>
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Media KM/Voo</p><p className="text-2xl font-rajdhani font-bold text-blue-600">{stats.realized > 0 ? (parseFloat(stats.totalKm) / stats.realized).toFixed(1) : '0'}</p></div>
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Turno Mais Ativo</p><p className="text-2xl font-rajdhani font-bold text-gold">{chartData.shiftData.reduce((a, b) => a.value > b.value ? a : b).name}</p></div>
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Eficiencia</p><p className="text-2xl font-rajdhani font-bold text-green-600">{stats.activePilots > 0 ? (stats.totalFlights / stats.activePilots).toFixed(1) : '0'} v/p</p></div>
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Anotacoes</p><p className="text-2xl font-rajdhani font-bold text-amber-600">{stats.withNotes}</p></div>
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Ocorrencias</p><p className="text-2xl font-rajdhani font-bold text-red-500">{stats.withOccurrences}</p></div>
              <div><p className="text-gray-500 text-xs font-mono uppercase mb-1">Total Pontos</p><p className="text-2xl font-rajdhani font-bold text-blue-500">{stats.totalRegistros}</p></div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TimePage;
